import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import {
  activateCustomerUser,
  getCustomerInvitations,
  getCustomerUsers,
  inviteCustomerUser,
  removeCustomerUser,
  resendCustomerInvitation,
  revokeCustomerInvitation,
  suspendCustomerUser,
} from '../api/authApi';
import { DataTable } from './DataTable';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { TableToolbar } from './TableToolbar';
import { Button } from './ui/Button';
import { formatCustomerRole } from '../utils/customerPortalLabels';

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
}

function InviteUserDialog({ open, onClose, customerId, onInvited }) {
  const { t } = useTranslation(['customers', 'customerInvitations', 'common']);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'customer_admin',
    is_primary_contact: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [invitationEmail, setInvitationEmail] = useState(null);

  const reset = () => {
    setForm({ name: '', email: '', role: 'customer_admin', is_primary_contact: false });
    setError('');
    setInvitationLink('');
    setInvitationEmail(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);

    try {
      const result = await inviteCustomerUser(customerId, form);
      setInvitationLink(result.invitation_link || result.data?.invitation_link || result.invitation?.invitation_link || '');
      setInvitationEmail(result.invitation_email || result.data?.invitation_email || null);
      onInvited();
    } catch (err) {
      setError(err.response?.data?.message || t('customers:invite_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (invitationLink) {
      await navigator.clipboard.writeText(invitationLink);
    }
  };

  const emailStatus = invitationEmail?.status || '';
  const emailAlertSeverity = emailStatus === 'sent'
    ? 'success'
    : (emailStatus === 'skipped' ? 'warning' : (emailStatus === 'failed' ? 'error' : 'warning'));
  const emailMessage = invitationEmail?.message_key
    ? t(invitationEmail.message_key.replace(/^customerInvitations\./, 'customerInvitations:'), {
      defaultValue: invitationEmail.message,
    })
    : (emailStatus === 'sent'
      ? t('customerInvitations:email.sent')
      : t('customers:invitationLinkFallback'));

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('customers:invite_dialog_title')}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {invitationLink ? (
          <Alert severity={emailAlertSeverity}>
            {emailMessage}
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                {invitationLink}
              </Typography>
              <IconButton onClick={copyLink} size="small" aria-label={t('customers:copy_invitation_link')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Alert>
        ) : (
          <>
            <TextField label={t('common:name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
            <TextField label={t('common:email')} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required fullWidth />
            <FormControl fullWidth>
              <InputLabel id="invite-role-label">{t('common:role')}</InputLabel>
              <Select
                labelId="invite-role-label"
                label={t('common:role')}
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <MenuItem value="customer_admin">{t('customers:role_customer_admin')}</MenuItem>
                <MenuItem value="customer_user">{t('customers:role_customer_user')}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.is_primary_contact}
                  onChange={(e) => setForm({ ...form, is_primary_contact: e.target.checked })}
                />
              )}
              label={t('customers:primary_contact')}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="Ghost" onClick={handleClose}>
          {invitationLink ? t('common:close') : t('common:cancel')}
        </Button>
        {!invitationLink && (
          <Button variant="Primary" onClick={handleSubmit} loading={submitting}>
            {t('customers:invite')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function invitationEmailStatusLabel(t, status) {
  switch (status) {
    case 'sent':
      return t('customerInvitations:status.sent');
    case 'failed':
      return t('customerInvitations:status.failed');
    case 'skipped':
      return t('customerInvitations:status.skipped');
    case 'disabled':
      return t('customerInvitations:status.disabled');
    default:
      return t('customerInvitations:status.notSent');
  }
}

const actionBtnSx = { minHeight: 32, px: 1.25, fontSize: 12 };

export function CustomerUsersTab({ customerId }) {
  const { t } = useTranslation(['customers', 'customerInvitations', 'common', 'statuses']);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [resendConfirm, setResendConfirm] = useState(null);
  const [resending, setResending] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [usersResponse, invitationsResponse] = await Promise.all([
        getCustomerUsers(customerId),
        getCustomerInvitations(customerId),
      ]);
      setUsers(usersResponse.data || []);
      setInvitations(invitationsResponse.data || []);
    } catch {
      setError(t('customers:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = useCallback(async (action) => {
    try {
      await action();
      await loadData();
    } catch {
      setSnackbar(t('customers:action_failed'));
    }
  }, [loadData, t]);

  const copyInvitationLink = useCallback(async (invitation) => {
    const value = typeof invitation?.activation_url === 'string'
      ? invitation.activation_url.trim()
      : (typeof invitation?.invitation_link === 'string' ? invitation.invitation_link.trim() : '');

    if (!value || !/^https?:\/\//i.test(value)) {
      setSnackbar(t('customerInvitations:messages.activationUrlMissing'));
      return;
    }

    await navigator.clipboard.writeText(value);
    setSnackbar(t('customerInvitations:messages.linkCopied'));
  }, [t]);

  const handleResend = async () => {
    if (!resendConfirm?.id) {
      return;
    }

    setResending(true);
    try {
      const result = await resendCustomerInvitation(resendConfirm.id);
      const invitationRow = result.data || {};
      const link = (typeof result.activation_url === 'string' && result.activation_url)
        || (typeof result.invitation_link === 'string' && result.invitation_link)
        || (typeof invitationRow.activation_url === 'string' && invitationRow.activation_url)
        || '';
      const email = result.invitation_email || {};
      const emailStatus = email.status;
      const messageKey = email.message_key;

      if (messageKey) {
        setSnackbar(t(messageKey.replace(/^customerInvitations\./, 'customerInvitations:'), {
          defaultValue: email.message || '',
        }));
      } else if (emailStatus === 'sent') {
        setSnackbar(t('customerInvitations:email.sent'));
      } else if (emailStatus === 'skipped') {
        setSnackbar(t('customerInvitations:email.sandboxRecipientNotAllowed'));
      } else if (emailStatus === 'failed' || emailStatus === 'disabled') {
        setSnackbar(t('customerInvitations:email.failed'));
      } else if (emailStatus === 'not_sent') {
        setSnackbar(t('customerInvitations:email.notSent'));
      }

      if (typeof link === 'string' && /^https?:\/\//i.test(link) && emailStatus !== 'sent') {
        await navigator.clipboard.writeText(link);
      }

      setResendConfirm(null);
      await loadData();
    } catch {
      setSnackbar(t('customers:resend_failed'));
      setResendConfirm(null);
    } finally {
      setResending(false);
    }
  };

  const userColumns = useMemo(() => [
    {
      id: 'name',
      label: t('common:name'),
      render: (user) => user.name || '—',
    },
    {
      id: 'email',
      label: t('common:email'),
      render: (user) => user.email || '—',
    },
    {
      id: 'role',
      label: t('common:role'),
      render: (user) => formatCustomerRole(user.role) || '—',
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (user) => <StatusBadge status={user.membership_status} category="membership" size="Sm" />,
    },
    {
      id: 'primary',
      label: t('customers:primary'),
      render: (user) => (user.is_primary_contact ? t('statuses:yes') : t('statuses:no')),
    },
    {
      id: 'invited_at',
      label: t('customers:invited_at'),
      render: (user) => formatDate(user.invited_at),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (user) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap' }}>
          {user.membership_status === 'active' && (
            <Button
              variant="Ghost"
              sx={actionBtnSx}
              onClick={() => handleAction(() => suspendCustomerUser(customerId, user.id))}
            >
              {t('customers:suspend')}
            </Button>
          )}
          {user.membership_status === 'suspended' && (
            <Button
              variant="Ghost"
              sx={actionBtnSx}
              onClick={() => handleAction(() => activateCustomerUser(customerId, user.id))}
            >
              {t('customers:activate')}
            </Button>
          )}
          <Button
            variant="Danger"
            sx={actionBtnSx}
            onClick={() => handleAction(() => removeCustomerUser(customerId, user.id))}
          >
            {t('customers:remove')}
          </Button>
        </Box>
      ),
    },
  ], [customerId, handleAction, t]);

  const invitationColumns = useMemo(() => [
    {
      id: 'email',
      label: t('common:email'),
      render: (invitation) => invitation.email || '—',
    },
    {
      id: 'role',
      label: t('common:role'),
      render: (invitation) => formatCustomerRole(invitation.role) || invitation.role,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (invitation) => <StatusBadge status={invitation.status} category="invitation" size="Sm" />,
    },
    {
      id: 'email_status',
      label: t('customerInvitations:labels.emailStatus'),
      render: (invitation) => (
        <Box>
          <Typography variant="body2">
            {invitationEmailStatusLabel(t, invitation.email_send_status)}
          </Typography>
          {(!invitation.email_send_status || invitation.email_send_status === 'not_sent') && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t('customerInvitations:email.notSent')}
            </Typography>
          )}
          {invitation.email_send_status === 'failed' && invitation.email_safe_error_message && (
            <Typography variant="caption" color="error.main" display="block">
              {invitation.email_safe_error_message}
            </Typography>
          )}
          {invitation.email_send_status === 'skipped' && (
            <Typography variant="caption" color="warning.main" display="block">
              {invitation.email_safe_error_message
                || t('customerInvitations:email.sandboxRecipientNotAllowed')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'last_sent',
      label: t('customerInvitations:labels.lastSentAt'),
      render: (invitation) => (
        invitation.email_sent_at || invitation.email_last_attempt_at
          ? formatDate(invitation.email_sent_at || invitation.email_last_attempt_at)
          : t('common:empty_value')
      ),
    },
    {
      id: 'expires',
      label: t('customers:expires'),
      render: (invitation) => formatDate(invitation.expires_at),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (invitation) => (
        invitation.status === 'pending' ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap' }}>
            <Button
              variant="Ghost"
              sx={actionBtnSx}
              disabled={!invitation.activation_url && !invitation.token_present}
              onClick={() => copyInvitationLink(invitation)}
            >
              {t('customerInvitations:actions.copyLink')}
            </Button>
            <Button
              variant="Ghost"
              sx={actionBtnSx}
              onClick={() => setResendConfirm({ id: invitation.id, email: invitation.email })}
            >
              {t('customerInvitations:actions.resend')}
            </Button>
            <Button
              variant="Secondary"
              sx={actionBtnSx}
              onClick={() => handleAction(() => revokeCustomerInvitation(invitation.id))}
            >
              {t('customers:revoke')}
            </Button>
          </Box>
        ) : null
      ),
    },
  ], [copyInvitationLink, handleAction, t]);

  if (loading) {
    return <LoadingSkeleton type="Table" rows={3} columns={5} />;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <SectionCard variant="Default" noPadding sx={{ mb: 3 }}>
        <TableToolbar
          title={t('customers:users_title')}
          actions={(
            <Button variant="Primary" onClick={() => setInviteOpen(true)}>
              {t('customers:invite_user')}
            </Button>
          )}
        />
        <DataTable
          type="Default"
          embedded
          columns={userColumns}
          rows={users}
          emptyMessage={t('customers:no_users')}
        />
      </SectionCard>

      <SectionCard variant="Default" noPadding>
        <TableToolbar title={t('customers:invitations_title')} />
        <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
          <Alert severity="info" sx={{ mb: 0 }}>
            {t('customerInvitations:email.sandboxRecipientNotAllowed')}
          </Alert>
        </Box>
        <DataTable
          type="Default"
          embedded
          columns={invitationColumns}
          rows={invitations}
          emptyMessage={t('customers:no_invitations')}
        />
      </SectionCard>

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        customerId={customerId}
        onInvited={loadData}
      />

      <Dialog open={Boolean(resendConfirm)} onClose={() => !resending && setResendConfirm(null)}>
        <DialogTitle>{t('customerInvitations:actions.resend')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('customerInvitations:confirmations.resend', { email: resendConfirm?.email || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="Ghost" onClick={() => setResendConfirm(null)} disabled={resending}>
            {t('common:cancel')}
          </Button>
          <Button variant="Primary" onClick={handleResend} disabled={resending} loading={resending}>
            {t('customerInvitations:actions.resend')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snackbar)} autoHideDuration={4000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
}
